// Mellowby quick-add: opens a sheet with variant pickers for any product card,
// submits to /cart/add.js, and refreshes Dawn's cart drawer + icon-bubble.

(function () {
  const collectionCache = new Map();
  const pinnedProductCache = new Map();
  const PIN_PARAM_NAMES = ['pin', 'pinned', 'featured_product', 'product_handle', 'product'];
  let collectionAbort = null;
  let collectionSubmitTimer = null;
  let lastFilterTrigger = null;

  function getPinParam(urlLike = window.location.href) {
    if (!window.URL || !window.URLSearchParams) return null;

    const url = new URL(urlLike, window.location.origin);
    for (const name of PIN_PARAM_NAMES) {
      const value = (url.searchParams.get(name) || '').trim();
      if (value) return { name, value };
    }
    return null;
  }

  function normalizePinnedHandle(value) {
    if (!value) return '';
    let handle = String(value).trim();
    try {
      if (/^https?:\/\//i.test(handle) || handle.charAt(0) === '/') {
        const url = new URL(handle, window.location.origin);
        const match = url.pathname.match(/\/products\/([^/]+)/);
        handle = match ? match[1] : handle;
      }
    } catch (e) {}
    try {
      handle = decodeURIComponent(handle);
    } catch (e) {}
    return handle
      .replace(/^\/+|\/+$/g, '')
      .split('?')[0]
      .split('#')[0]
      .toLowerCase();
  }

  function getPinnedHandle(urlLike = window.location.href) {
    const pin = getPinParam(urlLike);
    return pin ? normalizePinnedHandle(pin.value) : '';
  }

  function preservePinParam(url) {
    const current = getPinParam();
    if (!current) return;
    const alreadyPinned = PIN_PARAM_NAMES.some((name) => url.searchParams.has(name));
    if (!alreadyPinned) url.searchParams.set(current.name, current.value);
  }

  function formatPinnedPrice(cents) {
    const amount = Number(cents || 0) / 100;
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch (e) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  function normalizeProductImageUrl(url) {
    if (!url) return '';
    if (typeof url === 'object' && url.src) return normalizeProductImageUrl(url.src);
    const src = String(url);
    if (src.indexOf('//') === 0) return `${window.location.protocol}${src}`;
    return src;
  }

  async function loadPinnedProduct(handle) {
    if (pinnedProductCache.has(handle)) return pinnedProductCache.get(handle);

    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const product = await response.json();
      pinnedProductCache.set(handle, product);
      return product;
    } catch (e) {
      return null;
    }
  }

  function buildPinnedProductItem(product, handle) {
    const firstAvailable = (product.variants || []).find((variant) => variant.available) || (product.variants || [])[0];
    const image = normalizeProductImageUrl(
      (firstAvailable && firstAvailable.featured_image && firstAvailable.featured_image.src) ||
      product.featured_image ||
      (product.images && product.images[0])
    );
    const title = product.title || handle;
    const href = product.url || `/products/${handle}`;
    const kicker = product.type || 'Featured';
    const price = product.price_min || product.price || (firstAvailable && firstAvailable.price) || 0;
    const available = product.available !== false && Boolean(firstAvailable);

    const item = document.createElement('li');
    item.className = 'mel-collection-grid__item is-pinned-by-url';
    item.setAttribute('data-mel-pin-injected', 'true');
    item.innerHTML = `
      <a class="mel-design-card" href="${escapeHtml(href)}" title="${escapeHtml(title)}">
        <div class="mel-design-card__art">
          ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(title)}" loading="lazy" width="800" height="800">` : ''}
        </div>
        <div class="mel-design-card__body">
          <span class="mel-design-card__theme">${escapeHtml(kicker)}</span>
          <h3 class="mel-design-card__title" title="${escapeHtml(title)}">${escapeHtml(title)}</h3>
          <span class="mel-design-card__price">From ${escapeHtml(formatPinnedPrice(price))}</span>
        </div>
      </a>
      ${available ? `
        <button type="button"
                class="mel-quick-add-btn"
                data-handle="${escapeHtml(handle)}"
                data-product-id="${escapeHtml(product.id)}"
                data-kicker="${escapeHtml(kicker)}"
                aria-label="Quick add ${escapeHtml(title)} to cart">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M5 12h14M12 5v14" stroke="currentColor" stroke-width="2.2" fill="none" stroke-linecap="round"></path>
          </svg>
          <span>Quick add</span>
        </button>` : ''}
    `;
    return item;
  }

  async function pinCollectionProduct() {
    const grid = document.querySelector('.mel-collection-grid');
    if (!grid) return false;

    grid.querySelectorAll('[data-mel-pin-injected]').forEach((item) => item.remove());

    const handle = getPinnedHandle();
    if (!handle) return false;

    const pinnedButton = Array.from(grid.querySelectorAll('.mel-quick-add-btn[data-handle]')).find((button) =>
      normalizePinnedHandle(button.dataset.handle) === handle
    );
    const pinnedItem = pinnedButton && pinnedButton.closest('.mel-collection-grid__item');
    if (pinnedItem) {
      pinnedItem.classList.add('is-pinned-by-url');
      if (grid.firstElementChild !== pinnedItem) grid.prepend(pinnedItem);
      return true;
    }

    const product = await loadPinnedProduct(handle);
    if (!product || !document.contains(grid) || getPinnedHandle() !== handle) return false;
    grid.prepend(buildPinnedProductItem(product, handle));
    return true;
  }

  function normalizeLegacyPriceParams() {
    if (!window.URL || !window.URLSearchParams) return;

    const url = new URL(window.location.href);
    let minRaw = url.searchParams.get('filter.v.price.gte');
    let maxRaw = url.searchParams.get('filter.v.price.lte');
    let changed = false;

    if (minRaw === '') {
      url.searchParams.delete('filter.v.price.gte');
      minRaw = null;
      changed = true;
    }

    if (maxRaw === '') {
      url.searchParams.delete('filter.v.price.lte');
      maxRaw = null;
      changed = true;
    }

    const minValue = parseFloat(minRaw);
    const maxValue = parseFloat(maxRaw);
    const hasZeroOnlyPrice =
      (minRaw !== null || maxRaw !== null) &&
      (minRaw === null || !Number.isFinite(minValue) || minValue === 0) &&
      (maxRaw === null || !Number.isFinite(maxValue) || maxValue === 0);

    if (hasZeroOnlyPrice) {
      url.searchParams.delete('filter.v.price.gte');
      url.searchParams.delete('filter.v.price.lte');
      changed = true;
    }

    if (changed) {
      url.searchParams.delete('page');
      window.location.replace(url.toString());
    }
  }

  function syncPriceInputs(form) {
    form.querySelectorAll('[data-price-range-input]').forEach((input) => {
      const paramName = input.dataset.paramName || input.getAttribute('name');
      if (!paramName) return;
      if (!input.dataset.paramName) input.dataset.paramName = paramName;

      const raw = input.value.trim();
      const value = parseFloat(raw);
      if (raw === '' || !Number.isFinite(value) || value === 0) {
        input.removeAttribute('name');
      } else {
        input.setAttribute('name', paramName);
      }
    });
  }

  function normalizeColorParams(forms) {
    if (!window.URL || !window.URLSearchParams) return;

    const colorNames = new Set();
    forms.forEach((form) => {
      form.querySelectorAll('.mel-filter-bar__chip--color input[name]').forEach((input) => {
        colorNames.add(input.name);
      });
    });

    if (!colorNames.size) return;

    const url = new URL(window.location.href);
    let changed = false;
    colorNames.forEach((name) => {
      const values = url.searchParams.getAll(name);
      if (values.length <= 1) return;

      const selected = values[values.length - 1];
      url.searchParams.delete(name);
      url.searchParams.append(name, selected);
      changed = true;
    });

    if (changed) {
      url.searchParams.delete('page');
      window.location.replace(url.toString());
    }
  }

  function syncColorInputs(form, changedInput) {
    const input = changedInput && changedInput.closest('.mel-filter-bar__chip--color input[name]');
    if (!input || !input.checked) return;

    form.querySelectorAll('.mel-filter-bar__chip--color input[name]').forEach((other) => {
      if (other.name !== input.name) return;
      if (other !== input) {
        other.checked = false;
        const otherLabel = other.closest('.mel-filter-bar__swatch');
        if (otherLabel) otherLabel.classList.remove('is-active');
      }
    });

    const label = input.closest('.mel-filter-bar__swatch');
    if (label) label.classList.add('is-active');
  }

  function syncFilterOptionStates(form) {
    form.querySelectorAll('.mel-filter-bar__swatch, .mel-filter-bar__size, .mel-filter-bar__option').forEach((option) => {
      const input = option.querySelector('input');
      if (input) option.classList.toggle('is-active', input.checked);
    });
  }

  function isMobileViewport() {
    return window.matchMedia ? window.matchMedia('(max-width: 749px)').matches : window.innerWidth < 750;
  }

  function isMobileFilterOpen() {
    return isMobileViewport() && document.documentElement.classList.contains('mel-filter-open');
  }

  function getFocusableElements(root) {
    if (!root) return [];
    return Array.from(root.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
    )).filter((el) => el.getClientRects().length > 0 || el === document.activeElement);
  }

  function resetFilterForms() {
    document.querySelectorAll('[data-filter-form]').forEach((form) => {
      form.reset();
      syncPriceInputs(form);
      syncFilterOptionStates(form);
      form.classList.remove('has-pending-changes');
    });
  }

  function getCollectionWrapper() {
    const section = document.querySelector('.mel-collection-grid-section');
    return section ? section.closest('.shopify-section') || section : null;
  }

  function getCollectionSectionId() {
    const wrapper = getCollectionWrapper();
    if (!wrapper) return '';
    if (wrapper.id && wrapper.id.indexOf('shopify-section-') === 0) {
      return wrapper.id.replace('shopify-section-', '');
    }

    const section = wrapper.matches('.mel-collection-grid-section') ? wrapper : wrapper.querySelector('.mel-collection-grid-section');
    return section && section.dataset.sectionId ? section.dataset.sectionId : '';
  }

  function supportsCollectionAjax() {
    return Boolean(window.fetch && window.DOMParser && window.history && window.URL && getCollectionSectionId());
  }

  function setCollectionLoading(isLoading) {
    const section = document.querySelector('.mel-collection-grid-section');
    if (section) {
      section.classList.toggle('is-loading', isLoading);
      section.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
    document.querySelectorAll('[data-filter-form]').forEach((form) => {
      form.classList.toggle('is-submitting', isLoading);
    });
  }

  function closeFilterAside(options = {}) {
    const wasOpen = document.documentElement.classList.contains('mel-filter-open');
    const aside = document.getElementById('MelFilterAside');
    if (options.resetPending) resetFilterForms();
    if (aside) {
      aside.classList.remove('is-open');
      if (isMobileViewport()) {
        aside.setAttribute('aria-hidden', 'true');
      } else {
        aside.removeAttribute('aria-hidden');
      }
      aside.removeAttribute('aria-modal');
      aside.removeAttribute('role');
    }
    document.documentElement.classList.remove('mel-filter-open');
    const backdrop = document.querySelector('.mel-collection-aside__backdrop');
    if (backdrop) backdrop.hidden = true;
    if (wasOpen && options.returnFocus !== false) {
      const focusTarget =
        (lastFilterTrigger && document.contains(lastFilterTrigger) && lastFilterTrigger) ||
        document.querySelector('[data-mel-filter-open]');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }
  }

  function openFilterAside(trigger) {
    const aside = document.getElementById('MelFilterAside');
    if (!aside) return;

    lastFilterTrigger = trigger || document.activeElement;
    aside.classList.add('is-open');
    aside.removeAttribute('aria-hidden');
    aside.setAttribute('role', 'dialog');
    aside.setAttribute('aria-modal', 'true');
    document.documentElement.classList.add('mel-filter-open');

    const backdrop = document.querySelector('.mel-collection-aside__backdrop');
    if (backdrop) backdrop.hidden = false;

    window.setTimeout(() => {
      const closeButton = aside.querySelector('[data-mel-filter-close]');
      if (closeButton) closeButton.focus({ preventScroll: true });
    }, 0);
  }

  function trapFilterFocus(event) {
    if (!isMobileFilterOpen()) return;
    const aside = document.getElementById('MelFilterAside');
    if (!aside) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeFilterAside({ resetPending: true });
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements(aside);
    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function scrollToCollectionTop() {
    const target = document.querySelector('.mel-collection-toolbar') || document.querySelector('.mel-collection-grid-section');
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.pageYOffset - 88;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  function buildUrlFromForm(form) {
    syncPriceInputs(form);
    const base = form.getAttribute('action') || window.location.pathname;
    const url = new URL(base, window.location.origin);
    const formData = new FormData(form);

    url.search = '';
    formData.forEach((value, name) => {
      if (!name || value === '') return;
      url.searchParams.append(name, value);
    });
    preservePinParam(url);
    url.searchParams.delete('page');
    return url;
  }

  function withSectionParam(url) {
    const sectionId = getCollectionSectionId();
    if (!sectionId) return null;

    const fetchUrl = new URL(url.toString());
    fetchUrl.searchParams.set('section_id', sectionId);
    return fetchUrl;
  }

  function renderCollectionHtml(html, options = {}) {
    const currentWrapper = getCollectionWrapper();
    if (!currentWrapper) return false;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextWrapper =
      (currentWrapper.id && parsed.getElementById(currentWrapper.id)) ||
      parsed.querySelector('.mel-collection-grid-wrap') ||
      parsed.querySelector('.mel-collection-grid-section');

    if (!nextWrapper) return false;
    currentWrapper.replaceWith(nextWrapper);
    if (options.closeFilter) closeFilterAside({ returnFocus: options.returnFocus });
    return true;
  }

  async function navigateCollection(url, pushState, options = {}) {
    if (!supportsCollectionAjax()) {
      window.location.href = url.toString();
      return;
    }

    const cleanUrl = new URL(url.toString());
    const fetchUrl = withSectionParam(cleanUrl);
    if (!fetchUrl) {
      window.location.href = cleanUrl.toString();
      return;
    }

    if (collectionAbort) collectionAbort.abort();
    const controller = new AbortController();
    collectionAbort = controller;
    setCollectionLoading(true);

    try {
      const cacheKey = fetchUrl.toString();
      let html = collectionCache.get(cacheKey);
      if (!html) {
        const response = await fetch(fetchUrl.toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        html = await response.text();
        collectionCache.set(cacheKey, html);
      }

      if (!renderCollectionHtml(html, options)) throw new Error('Collection section missing');
      if (pushState !== false) {
        history.pushState({ melCollectionUrl: cleanUrl.toString() }, '', cleanUrl.toString());
      }
      normalizeColorParams(Array.from(document.querySelectorAll('[data-filter-form]')));
      pinCollectionProduct();
      if (options.scrollToTop) scrollToCollectionTop();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[mel-filter] ajax navigation failed', error);
      window.location.href = cleanUrl.toString();
    } finally {
      if (collectionAbort === controller) {
        collectionAbort = null;
        setCollectionLoading(false);
      }
    }
  }

  function queueCollectionSubmit(form) {
    window.clearTimeout(collectionSubmitTimer);
    collectionSubmitTimer = window.setTimeout(() => {
      navigateCollection(buildUrlFromForm(form), true);
    }, 120);
  }

  function handleFilterChange(event) {
    const sort = event.target.closest('[data-mel-sort]');
    if (sort && supportsCollectionAjax()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      url.searchParams.delete('page');
      navigateCollection(url, true);
      return;
    }

    const input = event.target.closest('[data-filter-checkbox]');
    if (!input) return;

    const form = input.closest('[data-filter-form]');
    if (!form) return;

    syncColorInputs(form, input);
    syncPriceInputs(form);

    const option = input.closest('.mel-filter-bar__swatch, .mel-filter-bar__size, .mel-filter-bar__option');
    if (option) option.classList.toggle('is-active', input.checked);

    if (isMobileFilterOpen()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      form.classList.add('has-pending-changes');
      return;
    }

    if (!supportsCollectionAjax()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    queueCollectionSubmit(form);
  }

  function handleFilterInput(event) {
    const form = event.target.closest('[data-filter-form]');
    if (form && event.target.closest('[data-price-range-input]')) syncPriceInputs(form);
  }

  function handleFilterSubmit(event) {
    const form = event.target.closest('[data-filter-form]');
    if (!form || !supportsCollectionAjax()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    navigateCollection(buildUrlFromForm(form), true, {
      closeFilter: isMobileFilterOpen(),
      returnFocus: true,
      scrollToTop: false,
    });
  }

  function handleCollectionClick(event) {
    const openTrigger = event.target.closest('[data-mel-filter-open]');
    if (openTrigger) {
      if (!document.getElementById('MelFilterAside')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      openFilterAside(openTrigger);
      return;
    }

    const closeTrigger = event.target.closest('[data-mel-filter-close]');
    if (closeTrigger) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeFilterAside({ resetPending: true });
      return;
    }

    const link = event.target.closest(
      '.mel-active-filters a, .mel-filter-bar__clear, .mel-filter-bar__menu-clear, .mel-collection-paginate a'
    );
    if (!link || !supportsCollectionAjax()) return;

    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin || !url.pathname.includes('/collections/')) return;
    preservePinParam(url);

    event.preventDefault();
    event.stopImmediatePropagation();
    navigateCollection(url, true, {
      closeFilter: true,
      returnFocus: false,
      scrollToTop: Boolean(link.closest('.mel-collection-paginate')),
    });
  }

  function installFilterGuards() {
    normalizeLegacyPriceParams();
    const forms = Array.from(document.querySelectorAll('[data-filter-form]'));
    normalizeColorParams(forms);
    forms.forEach((form) => syncPriceInputs(form));
    pinCollectionProduct();

    document.addEventListener('input', handleFilterInput, true);
    document.addEventListener('change', handleFilterChange, true);
    document.addEventListener('submit', handleFilterSubmit, true);
    document.addEventListener('click', handleCollectionClick, true);
    document.addEventListener('keydown', trapFilterFocus, true);
    window.addEventListener('popstate', () => {
      // This asset also runs on product/cart pages. Leave their history to the
      // browser instead of assigning the current URL again via the fallback.
      if (!supportsCollectionAjax()) return;
      navigateCollection(new URL(window.location.href), false);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installFilterGuards);
  } else {
    installFilterGuards();
  }
})();

(function () {
  const PURCHASE_PATH = /^\/(products|collections|cart|checkout)(\/|$)/;

  function cleanStalePageCacheArtifacts() {
    document.querySelectorAll('a.mel-header__icon[href="#"][aria-label="Wishlist"]').forEach((link) => link.remove());

    document.querySelectorAll('img[srcset]').forEach((img) => {
      const srcset = img.getAttribute('srcset') || '';
      if (/(^|,)\s+\d+w/.test(srcset)) {
        img.removeAttribute('srcset');
        const src = img.getAttribute('src');
        if (src) img.src = src;
      }
    });

    if (!PURCHASE_PATH.test(window.location.pathname || '')) return;

    try {
      localStorage.setItem('mel_nlpopup_v1', String(Date.now()));
    } catch (e) {}

    const popup = document.getElementById('MelNewsletterPopup');
    if (popup) {
      popup.classList.remove('is-open');
      popup.hidden = true;
      popup.remove();
    }
  }

  function keepPurchaseFlowClear() {
    if (!PURCHASE_PATH.test(window.location.pathname || '')) return;
    cleanStalePageCacheArtifacts();
    window.setTimeout(cleanStalePageCacheArtifacts, 8500);
    window.setTimeout(() => {
      if (!document.querySelector('cart-drawer.active, cart-drawer.animate, .mel-quick-add.is-open, #MelFilterAside.is-open')) {
        document.body.style.overflow = '';
      }
    }, 9000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', keepPurchaseFlowClear);
  } else {
    keepPurchaseFlowClear();
  }
})();

(function () {
  const COLOR_HEX = {
    'army-green': '#3d4a2b', 'black': '#121212', 'navy': '#1d2a4d',
    'royal-blue': '#1f3da8', 'sand': '#d8c9a4', 'sport-grey': '#a9a9a9',
    'white': '#f5f5f0', 'brown': '#6b4523', 'dusty-rose': '#c4958a',
    'forest-mist': '#6b8c7a', 'golden-harvest': '#d4a020', 'graphite': '#555555',
    'pomegranate-blush': '#c44040', 'summer-sky': '#4a90c4', 'terracotta-clay': '#b5613a',
    'twilight-navy': '#1e2b50', 'urban-haze': '#888888', 'warm-sand': '#d4c49a',
    'washed-black': '#2c2c2c', 'washed-brown': '#7a5a3a', 'washed-green': '#4a6b4a',
    'washed-navy': '#1e2b50', 'red': '#c0392b', 'maroon': '#7b1a2a',
    'forest-green': '#2d4a1e', 'heather-grey': '#b0b0b0', 'light-blue': '#a8c8e8',
    'cardinal': '#8b1a2a', 'aegean-blue': '#3a7ca5', 'coral-red': '#e35d4e',
    'dark-grey-black': '#2a2a2a', 'ginger-yellow': '#d4a01e', 'grey': '#808080',
    'gray': '#808080', 'olive-khaki': '#6e6c39', 'pink': '#f0a4be',
    'purple': '#6d3a8c', 'sport-grey-black': '#3a3a3a',
  };

  const SECTION_TARGETS = {
    'cart-drawer': 'CartDrawer',
    'cart-icon-bubble': 'cart-icon-bubble',
  };

  class MelQuickAdd {
    constructor(root) {
      this.root = root;
      this.content = root.querySelector('[data-mel-quick-add-content]');
      this.template = root.querySelector('[data-mel-quick-add-template]');
      this.product = null;
      this.selected = {};
      this.cache = new Map();
      this.loadVersion = 0;
      this.adding = false;
      this.confirmationTimers = new WeakMap();

      document.addEventListener('click', this.onDocClick.bind(this));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.root.hidden) this.close();
      });
    }

    onDocClick(e) {
      const editTrigger = e.target.closest('[data-cart-edit]');
      if (editTrigger) {
        e.preventDefault();
        e.stopPropagation();
        this.openForEdit({
          handle: editTrigger.dataset.handle,
          lineKey: editTrigger.dataset.lineKey,
          variantId: editTrigger.dataset.variantId,
          quantity: parseInt(editTrigger.dataset.quantity, 10) || 1,
        });
        return;
      }
      const trigger = e.target.closest('.mel-quick-add-btn');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        this.openForHandle(trigger.dataset.handle, trigger.dataset.kicker, {
          color: trigger.dataset.selectedColor || '',
          direct: trigger.dataset.directAdd === 'true',
        });
        return;
      }
      if (e.target.closest('[data-mel-quick-add-close]')) {
        this.close();
      }
    }

    openForEdit({ handle, lineKey, variantId, quantity }) {
      if (!handle || !lineKey || !variantId) return;
      this.editContext = { lineKey, oldVariantId: String(variantId), oldQuantity: quantity };
      return this.openForHandle(handle, 'Edit your selection', { variantId: String(variantId) });
    }

    async openForHandle(handle, kicker, preselect = {}) {
      if (!handle || this.adding) return;
      const version = ++this.loadVersion;
      this.kicker = kicker || '';
      this.preselect = preselect || {};
      this.show();
      this.renderSkeleton();
      try {
        if (!this.cache.has(handle)) {
          const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const product = await res.json();
          if (version !== this.loadVersion) return;
          product.options = product.options.map((o) => (typeof o === 'string' ? o : o.name));
          this.cache.set(handle, product);
        }
        if (version !== this.loadVersion) return;
        this.product = this.cache.get(handle);
        this.selected = this.pickInitialVariant();
        this.render();
        if (preselect.direct && this.product.variants.length === 1 && this.product.variants[0].available && !this.product.requires_selling_plan) await this.addToCart();
      } catch (err) {
        if (version !== this.loadVersion) return;
        console.error('[mel-quick-add] failed to load', err);
        this.renderError('Could not load product. Please try the product page.');
      }
    }

    formatValue(v) {
      return String(v == null ? '' : v).replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }

    pickInitialVariant() {
      const sel = {};
      const preferredColor = this.preselect && this.preselect.color;
      const preferredVariantId = this.preselect && this.preselect.variantId;
      const colorOption = this.product.options.find((name) => /color|colour/i.test(name));
      const colorOptionIndex = colorOption ? this.product.options.indexOf(colorOption) : -1;
      let firstAvailable = null;

      if (preferredVariantId) {
        firstAvailable = this.product.variants.find((v) => String(v.id) === String(preferredVariantId));
      }

      if (!firstAvailable && preferredColor && colorOptionIndex >= 0) {
        firstAvailable =
          this.product.variants.find((v) => v[`option${colorOptionIndex + 1}`] === preferredColor && v.available) ||
          this.product.variants.find((v) => v[`option${colorOptionIndex + 1}`] === preferredColor);
      }

      firstAvailable = firstAvailable || this.product.variants.find((v) => v.available) || this.product.variants[0];
      this.product.options.forEach((name, i) => {
        sel[name] = firstAvailable[`option${i + 1}`];
      });
      return sel;
    }

    findVariant() {
      return this.product.variants.find((v) =>
        this.product.options.every((name, i) => v[`option${i + 1}`] === this.selected[name])
      );
    }

    valueAvailable(optionName, value) {
      // Check whether choosing this value (with current other selections) leads to an in-stock variant.
      const optionIdx = this.product.options.indexOf(optionName);
      return this.product.variants.some((v) => {
        if (v[`option${optionIdx + 1}`] !== value) return false;
        return this.product.options.every((other, i) => {
          if (i === optionIdx) return true;
          return v[`option${i + 1}`] === this.selected[other];
        }) && v.available;
      });
    }

    render() {
      const p = this.product;
      const variant = this.findVariant();
      const node = this.template.content.cloneNode(true);
      const view = node.querySelector('[data-qa-view]');

      const img = node.querySelector('[data-qa-image]');
      const featured = (variant && variant.featured_image && variant.featured_image.src) || p.featured_image;
      img.src = featured ? this.imgSize(featured, 540) : '';
      img.alt = p.title;

      node.querySelector('[data-qa-kicker]').textContent = this.kicker || p.type || 'Quick add';
      node.querySelector('[data-qa-title]').textContent = p.title;
      node.querySelector('[data-qa-price]').textContent = this.formatPrice(variant ? variant.price : p.price);

      node.querySelector('[data-qa-error]').setAttribute('role', 'alert');
      const optsRoot = node.querySelector('[data-qa-options]');
      const isSingle = p.variants.length === 1;
      if (!isSingle) {
        p.options.forEach((name, i) => {
          const isSize = /size/i.test(name);
          const isColor = /color|colour/i.test(name);
          const values = [...new Set(p.variants.map((v) => v[`option${i + 1}`]))];
          const group = document.createElement('div');
          group.className = 'mel-quick-add__option' + (isColor ? ' mel-quick-add__option--color' : '');
          group.innerHTML = `
            <div class="mel-quick-add__option-name">
              <span class="mel-quick-add__option-label">${escapeHtml(name)}</span>
              <span class="mel-quick-add__option-value">${escapeHtml(this.formatValue(this.selected[name] || ''))}</span>
            </div>
            <div class="mel-quick-add__option-values" role="radiogroup" aria-label="${escapeAttr(name)}"></div>`;
          const valuesEl = group.querySelector('.mel-quick-add__option-values');
          values.forEach((value) => {
            const active = this.selected[name] === value;
            const available = this.valueAvailable(name, value);
            const display = this.formatValue(value);
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.disabled = this.adding;
            btn.dataset.optionName = name;
            btn.dataset.optionValue = value;
            btn.setAttribute('role', 'radio');
            btn.setAttribute('aria-checked', active ? 'true' : 'false');
            if (isColor) {
              btn.className = 'mel-quick-add__swatch' + (active ? ' is-active' : '') + (available ? '' : ' is-unavailable');
              btn.title = display;
              btn.setAttribute('aria-label', display);
              btn.innerHTML = `<span style="background-color:${this.colorHex(value)}"></span>`;
            } else {
              btn.className = 'mel-quick-add__pill' + (active ? ' is-active' : '') + (available ? '' : ' is-unavailable');
              btn.textContent = isSize ? value : display;
            }
            btn.addEventListener('click', () => {
              this.selected[name] = value;
              this.render();
            });
            valuesEl.appendChild(btn);
          });
          optsRoot.appendChild(group);
        });
      }

      const submit = node.querySelector('[data-qa-submit]');
      const submitLabel = node.querySelector('[data-qa-submit-label]');
      const isEdit = Boolean(this.editContext);
      const activeLabel = isEdit ? 'Save changes' : 'Add to cart';
      if (!variant) {
        submitLabel.textContent = 'Choose options';
        submit.disabled = true;
      } else if (!variant.available) {
        submitLabel.textContent = 'Sold out';
        submit.disabled = true;
      } else {
        submitLabel.textContent = activeLabel;
        submit.disabled = this.adding;
      }
      submit.addEventListener('click', () => (isEdit ? this.editLine() : this.addToCart()));
      view.href = variant ? `/products/${p.handle}?variant=${variant.id}` : `/products/${p.handle}`;

      const focused = document.activeElement;
      const focusedOption = this.content.contains(focused) && focused.dataset.optionName ? { name: focused.dataset.optionName, value: focused.dataset.optionValue } : null;
      this.content.replaceChildren(node);
      if (focusedOption) [...this.content.querySelectorAll('[data-option-name]')].find(button => button.dataset.optionName === focusedOption.name && button.dataset.optionValue === focusedOption.value)?.focus();
      // Variant rendering changes the modal's first/last focusable controls.
      if (!this.root.hidden && typeof trapFocus === 'function') {
        const focusTarget = this.root.contains(document.activeElement) ? document.activeElement : this.root.querySelector('button[data-mel-quick-add-close]');
        // Dawn arms its boundary keys on focusin, including when the same
        // Close button remains focused while product options arrive.
        if (focusTarget === document.activeElement) focusTarget.blur();
        trapFocus(this.root, focusTarget);
      }
    }

    async addToCart() {
      if (this.adding) return;
      const variant = this.findVariant();
      if (!variant || !variant.available) return;
      this.adding = true;
      const submit = this.content.querySelector('[data-qa-submit]');
      const errorEl = this.content.querySelector('[data-qa-error]');
      const spinner = this.content.querySelector('.mel-quick-add__spinner');
      submit.disabled = true;
      submit.classList.add('is-loading');
      this.content.querySelectorAll('[data-option-name]').forEach(button => { button.disabled = true; });
      if (spinner) spinner.hidden = false;
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }

      try {
        const cartUi = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const formData = new FormData();
        const sections = cartUi && typeof cartUi.getSectionsToRender === 'function'
          ? cartUi.getSectionsToRender().map((section) => section.id).join(',')
          : Object.keys(SECTION_TARGETS).join(',');

        formData.append('id', variant.id);
        formData.append('quantity', '1');
        if (sections) formData.append('sections', sections);
        formData.append('sections_url', window.location.pathname);
        if (cartUi && typeof cartUi.setActiveElement === 'function') {
          cartUi.setActiveElement(this.trigger?.isConnected ? this.trigger : document.activeElement);
        }

        const res = await fetch(window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add', {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });
        const data = await this.parseCartResponse(res);
        if (!res.ok || data.status) {
          const msg = data.description || data.message || 'Could not add to cart.';
          throw new Error(msg);
        }

        this.close();
        // Match product-form.js: drop the is-empty class on the outer
        // <cart-drawer>. CartDrawer.renderContents only clears it on
        // .drawer__inner, so without this the empty-state grid layout (and
        // hidden header) sticks around and items render but appear missing.
        const drawerEl = document.querySelector('cart-drawer');
        if (drawerEl) drawerEl.classList.remove('is-empty');
        // The cart page already shows the full basket. Its shared cart-update
        // subscribers refresh page lines, totals, shipping and the hidden bag.
        // Collection and product-page adds keep the normal drawer feedback.
        if (!this.isCartPage()) {
          if (cartUi && data.sections && typeof cartUi.renderContents === 'function') {
            cartUi.renderContents(data);
          } else if (data.sections) {
            for (const [name, html] of Object.entries(data.sections)) {
              this.swapSection(SECTION_TARGETS[name] || name, html);
            }
            if (drawerEl && typeof drawerEl.open === 'function') {
              drawerEl.open();
            } else if (drawerEl) {
              drawerEl.classList.add('animate', 'active');
              drawerEl.removeAttribute('aria-hidden');
            }
          }
        }
        if (this.isCartPage()) this.confirmCartAdd();
        const trigger = this.trigger;
        const feedbackVersion = this.loadVersion;
        const productTitle = this.product.title;
        const detail = { variantId: variant.id };
        document.dispatchEvent(new CustomEvent('mel-quick-add:added', { detail }));
        if (this.isCartPage()) detail.cartUpdateComplete?.then(() => {
          this.revealCartAddition(trigger, variant.id, data.key, productTitle, feedbackVersion);
        }).catch(() => {});
      } catch (err) {
        console.error('[mel-quick-add] add failed', err);
        if (errorEl) { errorEl.textContent = err.message || 'Could not add to cart.'; errorEl.hidden = false; }
        submit.disabled = false;
        submit.classList.remove('is-loading');
        if (spinner) spinner.hidden = true;
      } finally {
        this.adding = false;
        this.content.querySelectorAll('[data-option-name]').forEach(button => { button.disabled = false; });
      }
    }

    async editLine() {
      const newVariant = this.findVariant();
      if (!newVariant || !this.editContext) return;
      const { lineKey, oldVariantId, oldQuantity } = this.editContext;

      // Same variant chosen — nothing to do.
      if (String(newVariant.id) === String(oldVariantId)) {
        this.close();
        return;
      }

      const submit = this.content.querySelector('[data-qa-submit]');
      const errorEl = this.content.querySelector('[data-qa-error]');
      const spinner = this.content.querySelector('.mel-quick-add__spinner');
      submit.disabled = true;
      submit.classList.add('is-loading');
      if (spinner) spinner.hidden = false;
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }

      // Add new variant first; only remove old line after the add succeeds.
      // If the second call fails, the user is left with both lines (recoverable)
      // rather than nothing.
      try {
        const addBody = new FormData();
        addBody.append('id', newVariant.id);
        addBody.append('quantity', String(oldQuantity));
        const addRes = await fetch(window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: addBody,
        });
        const addData = await this.parseCartResponse(addRes);
        if (!addRes.ok || addData.status) {
          throw new Error(addData.description || addData.message || 'Could not save selection.');
        }
      } catch (err) {
        console.error('[mel-quick-add] edit add failed', err);
        if (errorEl) { errorEl.textContent = err.message || 'Could not save selection.'; errorEl.hidden = false; }
        submit.disabled = false;
        submit.classList.remove('is-loading');
        if (spinner) spinner.hidden = true;
        return;
      }

      const cartUi = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const sections = cartUi && typeof cartUi.getSectionsToRender === 'function'
        ? cartUi.getSectionsToRender().map((s) => s.id).join(',')
        : Object.keys(SECTION_TARGETS).join(',');

      let data = null;
      try {
        const changeBody = new FormData();
        changeBody.append('id', lineKey);
        changeBody.append('quantity', '0');
        if (sections) changeBody.append('sections', sections);
        changeBody.append('sections_url', window.location.pathname);
        const changeRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: changeBody,
        });
        data = await this.parseCartResponse(changeRes);
        if (!changeRes.ok || data.status) {
          throw new Error(data.description || data.message || 'Could not remove old item.');
        }
      } catch (err) {
        // The new variant is in the cart but the old line couldn't be removed.
        // Don't block the user — close the modal and let the cart drawer refresh
        // so they can manually remove the duplicate.
        console.error('[mel-quick-add] edit remove failed', err);
      }

      this.close();
      const drawerEl = document.querySelector('cart-drawer');
      if (drawerEl) drawerEl.classList.remove('is-empty');
      if (cartUi && data && data.sections && typeof cartUi.renderContents === 'function') {
        cartUi.renderContents(data);
      } else if (data && data.sections) {
        for (const [name, html] of Object.entries(data.sections)) {
          this.swapSection(SECTION_TARGETS[name] || name, html);
        }
      }
      document.dispatchEvent(new CustomEvent('mel-quick-add:edited', {
        detail: { lineKey, oldVariantId, newVariantId: newVariant.id },
      }));
    }

    isCartPage() {
      const root = window.Shopify?.routes?.root || '/';
      const cartUrl = window.routes?.cart_url || `${root}cart`;
      const path = new URL(cartUrl, window.location.origin).pathname.replace(/\/+$/, '');
      return window.location.pathname.replace(/\/+$/, '') === path;
    }

    confirmCartAdd() {
      const trigger = this.trigger;
      if (!trigger?.matches('.ms-card__quick-add')) return;
      if (!trigger.dataset.quickAddLabel) trigger.dataset.quickAddLabel = trigger.textContent.trim();
      window.clearTimeout(this.confirmationTimers.get(trigger));
      trigger.textContent = '✓ Added to cart';
      const card = trigger.closest('.ms-card');
      let status = card?.querySelector('[data-mel-cart-added-status]');
      if (!status && card) {
        status = document.createElement('span');
        status.className = 'visually-hidden';
        status.dataset.melCartAddedStatus = '';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        card.appendChild(status);
      }
      if (status) status.textContent = `${this.product.title} added to your cart.`;
      this.confirmationTimers.set(trigger, window.setTimeout(() => {
        if (trigger.isConnected) trigger.textContent = trigger.dataset.quickAddLabel;
      }, 2500));
    }

    keepCartConfirmationVisible(trigger) {
      // Only reveal the still-focused card if the updated mobile dock covers
      // it. Never move a shopper who has focused another item or left it above
      // or below the viewport while the cart was refreshing.
      if (!trigger?.isConnected || document.activeElement !== trigger || document.querySelector('cart-drawer.active, #MelQuickAdd.is-open')) return;
      const dock = document.querySelector('.ms-cart-checkout');
      const dockBox = dock?.getBoundingClientRect();
      if (!dockBox?.height) return;
      const box = trigger.getBoundingClientRect();
      if (box.top >= 0 && box.top < window.innerHeight && box.bottom > dockBox.top - 12) {
        window.scrollBy(0, box.bottom - dockBox.top + 12);
      }
    }

    revealCartAddition(trigger, variantId, lineKey, productTitle, version) {
      // Wait for the native rows to refresh, and don't pull a shopper back if
      // they have opened another picker, focused elsewhere, or scrolled away.
      if (version !== this.loadVersion || !this.isCartPage() || !trigger?.isConnected ||
          document.activeElement !== trigger || document.querySelector('cart-drawer.active, #MelQuickAdd.is-open')) return;
      const triggerBox = trigger.getBoundingClientRect();
      if (triggerBox.bottom <= 0 || triggerBox.top >= window.innerHeight) return;

      const rows = [...document.querySelectorAll('cart-items .cart-item')];
      const keyedRow = lineKey && rows.find(row => {
        const href = row.querySelector('cart-remove-button a[href]')?.getAttribute('href');
        return href && new URL(href, window.location.origin).searchParams.get('id') === lineKey;
      });
      const variantRows = rows.filter(row => [...row.querySelectorAll('[data-quantity-variant-id]')]
        .some(input => input.dataset.quantityVariantId === String(variantId)));
      // Cart discounts can change a key. A unique variant is a safe fallback;
      // multiple lines with different properties must never highlight a guess.
      const row = keyedRow || (variantRows.length === 1 ? variantRows[0] : null);
      const details = row?.querySelector('.cart-item__details');
      if (!details) {
        this.keepCartConfirmationVisible(trigger);
        return;
      }

      document.querySelectorAll('cart-items [data-mel-cart-added-confirmation]').forEach(node => node.remove());
      document.querySelectorAll('cart-items .mel-cart-added-row').forEach(node => node.classList.remove('mel-cart-added-row'));
      // Reuse the card's one live region instead of adding another announcement.
      const status = trigger.closest('.ms-card')?.querySelector('[data-mel-cart-added-status]') || document.createElement('p');
      status.className = 'mel-cart-added-confirmation';
      status.dataset.melCartAddedConfirmation = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-label', `${productTitle} added to your cart.`);
      status.setAttribute('tabindex', '-1');
      status.textContent = '✓ Added to your cart';
      details.prepend(status);
      row.classList.add('mel-cart-added-row');
      status.focus({ preventScroll: true });
      row.scrollIntoView({
        behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'instant' : 'smooth',
        block: 'center',
      });
    }

    async parseCartResponse(response) {
      const text = await response.text();
      if (!text) return {};

      try {
        return JSON.parse(text);
      } catch (err) {
        const contentType = (response.headers.get('content-type') || '').split(';')[0] || 'non-JSON';
        const status = response.status ? ` (HTTP ${response.status})` : '';
        throw new Error(`Cart request returned ${contentType} instead of JSON${status}.`);
      }
    }

    swapSection(targetId, html) {
      const target = document.getElementById(targetId);
      if (!target) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById(targetId);
      if (fresh) target.innerHTML = fresh.innerHTML;
    }

    show() {
      this.trigger = document.activeElement;
      this.root.hidden = false;
      this.root.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => {
        if (this.root.getAttribute('aria-hidden') === 'true') return;
        this.root.classList.add('is-open');
        if (typeof trapFocus === 'function') trapFocus(this.root, this.root.querySelector('button[data-mel-quick-add-close]'));
      });
      document.documentElement.classList.add('mel-quick-add-open');
    }

    close() {
      this.loadVersion++;
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.trigger?.isConnected ? this.trigger : null);
      this.root.classList.remove('is-open');
      this.root.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('mel-quick-add-open');
      this.editContext = null;
      setTimeout(() => { if (!this.root.classList.contains('is-open')) this.root.hidden = true; }, 200);
    }

    renderSkeleton() {
      this.content.innerHTML = `
        <div class="mel-quick-add__skeleton" aria-hidden="true">
          <div class="mel-quick-add__skeleton-art"></div>
          <div class="mel-quick-add__skeleton-line"></div>
          <div class="mel-quick-add__skeleton-line mel-quick-add__skeleton-line--short"></div>
        </div>`;
    }

    renderError(msg) {
      this.content.innerHTML = `<div class="mel-quick-add__fallback"><p>${escapeHtml(msg)}</p>
        <button type="button" class="mel-quick-add__submit" data-mel-quick-add-close>Close</button></div>`;
    }

    colorHex(name) {
      const k = String(name).toLowerCase().replace(/\s+/g, '-');
      return COLOR_HEX[k] || '#cccccc';
    }

    formatPrice(cents) {
      const currency = window.Shopify?.currency?.active || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(Number(cents) / 100);
      } catch (_) { return `${currency} ${(Number(cents) / 100).toFixed(2)}`; }
    }

    imgSize(url, w) {
      try {
        return url.replace(/(\.(?:jpe?g|png|webp))(\?.*)?$/i, `_${w}x$1$2`);
      } catch (e) {
        return url;
      }
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
  function escapeAttr(s) { return escapeHtml(s); }

  function init() {
    let root = document.getElementById('MelQuickAdd');
    if (!root && document.body) {
      root = document.createElement('div');
      root.className = 'mel-quick-add';
      root.id = 'MelQuickAdd';
      root.setAttribute('aria-hidden', 'true');
      root.setAttribute('data-mel-quick-add', '');
      root.hidden = true;
      root.innerHTML = `
        <div class="mel-quick-add__backdrop" data-mel-quick-add-close></div>
        <div class="mel-quick-add__sheet" role="dialog" aria-modal="true" aria-labelledby="MelQuickAddTitle">
          <button type="button" class="mel-quick-add__close" data-mel-quick-add-close aria-label="Close quick add">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"></path>
            </svg>
          </button>
          <div class="mel-quick-add__inner" data-mel-quick-add-content></div>
        </div>
        <template data-mel-quick-add-template>
          <div class="mel-quick-add__layout">
            <div class="mel-quick-add__art">
              <img src="" alt="" width="540" height="540" data-qa-image>
            </div>
            <div class="mel-quick-add__body">
              <span class="mellowby-kicker" data-qa-kicker></span>
              <h3 class="mel-quick-add__title" id="MelQuickAddTitle" data-qa-title></h3>
              <div class="mel-quick-add__price" data-qa-price></div>
              <div class="mel-quick-add__options" data-qa-options></div>
              <div class="mel-quick-add__error" data-qa-error hidden></div>
              <button type="button" class="mel-quick-add__submit" data-qa-submit>
                <span data-qa-submit-label>Add to cart</span>
                <svg class="mel-quick-add__spinner" viewBox="0 0 24 24" aria-hidden="true" hidden>
                  <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-dasharray="40 60"></circle>
                </svg>
              </button>
              <a class="mel-quick-add__view" data-qa-view>View full details -></a>
            </div>
          </div>
        </template>`;
      document.body.appendChild(root);
    }
    if (!root) return;
    if (root.dataset.melQuickAddInit === '1') return;
    root.dataset.melQuickAddInit = '1';
    new MelQuickAdd(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
